import React from "react";
import Link from "next/link";
import { BreadcrumbJsonLd } from "next-seo";
import MetaTags from "@/components/seo/meta-tags";
import SitemapLinkList from "@/components/Common/SitemapLinkList";
import {
  SITEMAP_SECTIONS,
  getSectionCounts,
  getSitemapData,
} from "@/utils/sitemapData";
import styles from "./Sitemap.module.scss";

const APP_URL = process.env.NEXT_PUBLIC_APP_URL;

const TITLE = "Sitemap | Elchemy";
const DESCRIPTION =
  "Browse every page on Elchemy — the full chemical catalogue, technical and safety data sheets, blogs, market insights, events and industries.";

const SitemapHub = ({
  sections,
  staticPages,
  industries,
  markets,
  canonical,
}) => (
  <>
    <MetaTags
      title={TITLE}
      description={DESCRIPTION}
      defaultCanonical={canonical}
      NoIndexMetaKey={false}
    />
    {/*
      Canonical is deliberately left to MetaTags, exactly as every other SEO page
      on the site does (see pages/markets/[region]/[slug].jsx).

      ⚠️ Known limitation, inherited and NOT introduced here: MetaTags assigns its
      canonical inside a useEffect, so the SSR HTML carries
      `<link rel="canonical" href="">` and the real URL only appears after
      hydration — on every page of the site. `defaultCanonical` does not change
      that. Emitting a second canonical from here was tried and rejected:
      next/head does not dedupe across separate <Head> instances, so the page
      shipped a real canonical beside an empty one, which is a worse signal than
      the empty one alone. Fixing it properly means editing a component shared by
      most of the site, so it stays a separate follow-up.
    */}
    <BreadcrumbJsonLd
      itemListElements={[
        { position: 1, name: "Home", item: `${APP_URL}/` },
        { position: 2, name: "Sitemap", item: canonical },
      ]}
    />

    <main className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Sitemap</h1>
        <p className={styles.intro}>
          Every page on Elchemy, grouped by type. Each section is split across
          numbered pages so the lists stay readable.
        </p>
      </header>

      <section className={styles.block} aria-labelledby="sitemap-sections">
        <h2 className={styles.sectionHeading} id="sitemap-sections">
          Browse by section
        </h2>
        <ul className={styles.cards}>
          {sections.map((section) => (
            <li key={section.key} className={styles.card}>
              <Link
                href={`/sitemap/${section.key}/1`}
                className={styles.cardLink}
              >
                {section.label}
              </Link>
              <p className={styles.cardMeta}>
                {section.total.toLocaleString("en")}{" "}
                {section.total === 1 ? "page" : "pages"} ·{" "}
                {section.totalPages}{" "}
                {section.totalPages === 1 ? "list" : "lists"}
              </p>
              <p className={styles.cardBlurb}>{section.blurb}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.block} aria-labelledby="sitemap-industries">
        <h2 className={styles.sectionHeading} id="sitemap-industries">
          Industries
        </h2>
        <SitemapLinkList items={industries} columns={3} />
      </section>

      {/*
        The /markets landing pages are orphan by design — no header, footer or
        menu links to them — so these are the only internal links they get
        anywhere on the site. Rendered on the hub rather than as a paginated
        section because 21 entries do not need splitting, and being one click
        from the footer is the point.
      */}
      {markets.length > 0 && (
        <section className={styles.block} aria-labelledby="sitemap-markets">
          <h2 className={styles.sectionHeading} id="sitemap-markets">
            Markets &amp; Capabilities
          </h2>
          <SitemapLinkList items={markets} columns={2} />
        </section>
      )}

      <section className={styles.block} aria-labelledby="sitemap-pages">
        <h2 className={styles.sectionHeading} id="sitemap-pages">
          Other pages
        </h2>
        <SitemapLinkList items={staticPages} columns={3} />
      </section>
    </main>
  </>
);

export default SitemapHub;

export async function getServerSideProps({ res }) {
  const canonical = `${APP_URL}/sitemap`;

  try {
    const data = await getSitemapData();
    const counts = getSectionCounts(data);

    // Only advertise sections that actually have data — never link to a card
    // whose page would 404.
    const sections = SITEMAP_SECTIONS.filter((section) => counts[section.key])
      .map((section) => ({
        key: section.key,
        label: section.label,
        blurb: section.blurb,
        total: counts[section.key].total,
        totalPages: counts[section.key].totalPages,
      }))
      .filter((section) => section.total > 0);

    // Every API-backed section failed. Still render if the local-constant lists
    // survive — markets/industries/static pages come from constants, not the
    // API, and the orphan /markets pages have no other internal link anywhere on
    // the site, so a 404 here would strand them. Only 404 when there is genuinely
    // nothing to link to, which keeps the "never serve a zero-link page" rule.
    const hasLocalLinks =
      (data.markets?.length ?? 0) +
        (data.industries?.length ?? 0) +
        (data.staticPages?.length ?? 0) >
      0;
    if (!sections.length && !hasLocalLinks) return { notFound: true };

    if (Object.keys(data.errors ?? {}).length) {
      console.error("[sitemap] degraded sections:", data.errors);
    }

    res.setHeader(
      "Cache-Control",
      "public, s-maxage=900, stale-while-revalidate=86400"
    );

    return {
      props: {
        sections,
        staticPages: data.staticPages ?? [],
        industries: data.industries ?? [],
        markets: data.markets ?? [],
        canonical,
      },
    };
  } catch (error) {
    console.error("[sitemap] hub failed:", error?.message ?? error);
    return { notFound: true };
  }
}
